import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Trash2, RefreshCw, Send, CheckCircle2, Clock, Copy } from "lucide-react";

/** Admin-only: invite standalone desktop logins. Each invited user gets an
 * activation email, picks their business name/email + password, and works in
 * a fully isolated desktop of their own. */
export default function LoginsApp() {
  const [rows, setRows] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/mgmt/desktop-users").then((r) => setRows(r.data))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/mgmt/desktop-users", { email: email.trim() });
      toast.success("Invite emailed — they'll pick their business name and password.");
      setEmail("");
      load();
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail) || ex.message); }
    finally { setBusy(false); }
  };

  const resend = async (u) => {
    try { await api.post(`/mgmt/desktop-users/${u.id}/resend`); toast.success("Invite re-sent."); }
    catch (ex) { toast.error(formatErr(ex.response?.data?.detail) || ex.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete the desktop login for ${u.login_email}? Their files and signature requests will be removed too.`)) return;
    try { await api.delete(`/mgmt/desktop-users/${u.id}`); load(); }
    catch (ex) { toast.error(formatErr(ex.response?.data?.detail) || ex.message); }
  };

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success("Activation link copied."); }
    catch { window.prompt("Copy the activation link:", link); }
  };

  return (
    <div className="h-full flex flex-col text-slate-800" data-testid="logins-app">
      <form onSubmit={invite} className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="person@email.com — invite a new desktop login"
          className="flex-1 border border-slate-300 rounded px-2.5 py-1.5 text-sm bg-white"
          data-testid="logins-invite-email"
        />
        <button type="submit" disabled={busy || !email.trim()}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded bg-sky-600 text-white disabled:opacity-50"
          data-testid="logins-invite-btn">
          <Send className="w-3.5 h-3.5" /> Invite
        </button>
        <button type="button" className="p-2 rounded hover:bg-slate-200" onClick={load} title="Refresh" data-testid="logins-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </form>
      <div className="flex-1 overflow-auto">
        {!rows && <p className="text-sm text-slate-400 text-center mt-8">Loading…</p>}
        {rows && rows.length === 0 && (
          <p className="text-sm text-slate-400 text-center mt-8 px-6">
            No desktop logins yet. Invite someone above — they get their own separate
            desktop with files and e-signing under their own business name.
          </p>
        )}
        {rows && rows.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100" data-testid={`logins-row-${u.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">
                {u.business_name || u.login_email}
                {u.business_name && <span className="text-slate-400 font-normal"> · {u.login_email}</span>}
              </p>
              {u.business_email && <p className="text-xs text-slate-500">Sends as: {u.business_name} · replies → {u.business_email}</p>}
            </div>
            {u.status === "active" ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                <Clock className="w-3.5 h-3.5" /> Invited
              </span>
            )}
            {u.status !== "active" && (
              <>
                {u.invite_link && (
                  <button type="button" className="p-1.5 rounded hover:bg-slate-100" title="Copy activation link"
                    onClick={() => copyLink(u.invite_link)} data-testid={`logins-copy-${u.id}`}>
                    <Copy className="w-4 h-4 text-slate-500" />
                  </button>
                )}
                <button type="button" className="p-1.5 rounded hover:bg-slate-100" title="Re-send invite email"
                  onClick={() => resend(u)} data-testid={`logins-resend-${u.id}`}>
                  <Send className="w-4 h-4 text-sky-600" />
                </button>
              </>
            )}
            <button type="button" className="p-1.5 rounded hover:bg-red-50" title="Delete login"
              onClick={() => remove(u)} data-testid={`logins-delete-${u.id}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">
        Desktop logins sign in at <strong>/desktop</strong> — completely separate from your desktop and files.
      </div>
    </div>
  );
}
